import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { KycDocumentDto } from "@/features/kyc/api/dto";
import { useReviewKycDocument } from "@/features/kyc/api/use-review-kyc-document";
import {
  createKycDocumentRejectSchema,
  type KycDocumentRejectFormValues,
} from "@/features/kyc/schemas/kyc-document.schema";

/**
 * Dialog de rejet d'une pièce justificative — le motif est OBLIGATOIRE
 * (guide §6.2 : un rejet doit être motivé). TanStack Form + schéma zod
 * mémoïsé sur la langue courante.
 */
export function KycDocumentRejectDialog({
  open,
  onOpenChange,
  kycCaseId,
  document,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kycCaseId: string;
  /** Pièce à rejeter (absente = dialog fermé). */
  document?: KycDocumentDto;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Les clés kyc.documents.* vivent dans features/kyc/i18n/kyc-documents.json
  // en attendant leur fusion dans src/locales par le coordinateur.
  const tDoc = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createKycDocumentRejectSchema(), [i18n.language]);

  const review = useReviewKycDocument();

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart d'un motif vide.
  const form = useForm({
    defaultValues: { reason: "" } as KycDocumentRejectFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success || !document) return;
      review.mutate(
        {
          kycCaseId,
          documentId: document.id,
          decision: "REJECTED",
          reason: parsed.data.reason,
        },
        {
          onSuccess: () => {
            notifSilence(tDoc("kyc.documents.feedback.rejected"));
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tDoc("kyc.documents.rejectDialog.title")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="reason"
            validators={{
              onChange: ({ value }) => {
                const result = schema.shape.reason.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="reason">
                  {tDoc("kyc.documents.rejectDialog.reason")}
                </Label>
                <Textarea
                  id="reason"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={review.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={review.isPending}>
              {review.isPending ? <ButtonSpinner /> : null}
              {t("common.reject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
